/* Client-side mirror of app/services/role_service.py's BUILTIN_ROLES +
 * SEED_ROLES (the "system"/access-role catalog - not the same thing as
 * role_catalog_service.py, which is a *different* catalog of statutory
 * mine-site positions used for org role-fill compliance and is served at
 * GET /role-catalog).
 *
 * KNOWN GAP: there is no admin-facing endpoint that serves role_service's
 * catalog over JSON (only /leader/assignable-roles exists, and it's scoped
 * to a team leader's own assignable set). The Jinja originals
 * (create_staff.html, user_edit_wizard.html) read this catalog live via
 * role_service.list_roles() / assignable_roles(), so they always include
 * custom roles a super admin has created through the Role Builder
 * (admin/roles.html) at runtime. This hardcoded list can't - a role created
 * there after this file was written simply won't appear in CreateUser's
 * Staff tab or UserEdit's role pickers until someone adds it here, or (the
 * real fix) the backend grows a GET /api/v1/admin/roles endpoint mirroring
 * role_service.list_roles() and this file is replaced with a query against
 * it. Flagged again at each call site below. */

export interface SystemRole {
  key: string
  title: string
  tier: 'platform' | 'government' | 'mining_ops'
  level: number
  description: string
}

// Sorted by level desc, title asc - matches role_service.list_roles()'s sort.
export const SYSTEM_ROLES: SystemRole[] = [
  { key: 'super_admin', title: 'Super Admin', tier: 'platform', level: 100,
    description: 'Platform owner. Full access to every feature and all data.' },
  { key: 'gov_director', title: 'Government Director', tier: 'government', level: 70,
    description: 'National government oversight across all regions.' },
  { key: 'gov_regional', title: 'Regional Officer', tier: 'government', level: 60,
    description: 'Government oversight scoped to a single region.' },
  { key: 'gov_inspector', title: 'Mines Inspector', tier: 'government', level: 50,
    description: 'Government inspector scoped to a single district.' },
  { key: 'team_leader', title: 'Team Leader', tier: 'mining_ops', level: 30,
    description: 'Leads a mining organisation and oversees everything under it.' },
  { key: 'site_manager', title: 'Site Manager', tier: 'mining_ops', level: 29,
    description: 'On-site operational manager. Oversees production, logistics and safety for the site.' },
  { key: 'project_manager', title: 'Project Manager', tier: 'mining_ops', level: 29,
    description: 'Project-level manager. Captures records and views organisation reports for oversight.' },
  { key: 'safety_officer', title: 'Safety Officer', tier: 'mining_ops', level: 28,
    description: 'Site safety lead (Competent Person / HSE). Files daily safety reports, incidents and hazards.' },
  { key: 'chief_security', title: 'Chief Security', tier: 'mining_ops', level: 27,
    description: 'Security lead for gate checks, truck verification and ID checks.' },
  { key: 'hr_officer', title: 'HR Officer', tier: 'mining_ops', level: 26,
    description: 'Human Resources for the cooperative - staff records, contracts, medical records, salaries and payroll.' },
  { key: 'company', title: 'Company', tier: 'mining_ops', level: 25,
    description: 'Company-level operator that oversees everything under the organisation.' },
  { key: 'data_analyst', title: 'Data Analyst', tier: 'mining_ops', level: 24,
    description: 'Organisation analyst who can view operational information and log expenses.' },
  { key: 'hr_assistant', title: 'HR Assistant', tier: 'mining_ops', level: 24,
    description: 'Assists HR - can view staff records and HR reports for the cooperative.' },
  { key: 'spotter', title: 'Spotter', tier: 'mining_ops', level: 18,
    description: 'Assists gate, traffic and load movement verification on site.' },
  { key: 'assistant_spotter', title: 'Assistant Spotter', tier: 'mining_ops', level: 16,
    description: 'Supports spotters with gate checks, truck movement checks and ID verification.' },
  { key: 'miner', title: 'Miner', tier: 'mining_ops', level: 10,
    description: 'Individual small-scale miner.' },
]

export function systemRoleTitle(key?: string): string {
  if (!key) return '—'
  const found = SYSTEM_ROLES.find((r) => r.key === key)
  if (found) return found.title
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

/* PATCH /api/v1/admin/users/<uid> validates the "role", "roles" and
 * "access_roles" fields against api/routes.py's _assignable_roles_for(actor)
 * - role_service.assignable_roles() for the acting super_admin, i.e. the
 * full active role catalog (every SEED_ROLES key plus any custom role
 * created through the Role Builder). This used to be a hardcoded 7-key
 * allow-list that silently excluded hr_officer, chief_security,
 * data_analyst, safety_officer, spotter and every other seeded operational
 * role - meaning an admin could set them at user-creation time
 * (CreateUser.tsx's full catalog picker) but never afterwards. Mirrors
 * SYSTEM_ROLES above rather than the 7-key subset; still doesn't include a
 * super admin's own custom roles (the KNOWN GAP at the top of this file -
 * there's no GET endpoint serving those over JSON yet), so a save assigning
 * one of those still needs the Jinja admin UI until that gap is closed. */
export const PATCH_ASSIGNABLE_ROLE_KEYS = SYSTEM_ROLES.map((r) => r.key)
